// redux imports
import store from 'store'
import { hydrateUniteFailure } from 'redux/Auth'
// module imports
import { userAgent } from 'modules/Browser'
// constant imports
import { UNAUTHORIZED } from 'constants/Errors'
import * as C from 'constants/ApiUrls'

/**
 * Parses the JSON returned by a network request
 *
 * @param  {object} response A response from a network request
 *
 * @return {object}          The parsed JSON from the request
 */
const parseJSON = (response) => {
  if (response.status === 204 || response.status === 205) {
    return null
  }
  return response.json()
}

/**
 * Creates a standardized error on bad network response
 *
 * @example <caption>For messenger style errors</caption>.
 * errors: {
 *   general: ['Invalid token']
 * }
 *
 * @example <caption>For messenger style errors</caption>.
 * errorMessages: [{
 *   code: 702,
 *   exceptionData: '{UniteApiTokenParser}...'
 *   message: 'An error has occurred',
 * }]
 *
 * @param {object} response   A response from a network request
 *
 * @returns {Promise<Error>} Returns the standard error
 */
const parseErrors = async (response) => {
  let messages = []
  const json = await response.json()

  const { errorMessages } = json

  if (errorMessages) {
    messages = errorMessages.map(
      (error) => `${error.code}: ${error.exceptionData}`,
    )
  }

  // handle multiple errors
  return {
    name: response.statusText,
    status: response.status,
    message: messages.join(', '),
  }
}

/**
 * Checks if a network request came back fine, and throws an error if not
 *
 * @param  {object} response   A response from a network request
 *
 * @return {object|undefined} Returns either the response, or throws an error
 */
const checkStatus = async (response) => {
  if (response.status >= 200 && response.status < 300) {
    return response
  }

  if (response.status === 401) {
    return Promise.reject(UNAUTHORIZED)
  }

  // TODO standardize response
  if (response.status === 404) {
    // eslint-disable-next-line prefer-promise-reject-errors
    return Promise.reject({
      status: response.status,
      name: response.statusText,
      message: 'VPN is needed',
    })
  }

  const error = await parseErrors(response)

  return Promise.reject(error)
}

/**
 * Interpolates all api partials to valid values
 *
 * @param {string} rawData
 * @returns {string}
 */
export const interpolate = (rawData) => {
  let data = rawData.toString()
  const {
    auth: { accountId: uAccountId, token, userId: uUserId },
    config: {
      servers: { API_UNITE, API_PUBLIC },
    },
  } = store.getState()

  if (data.includes(C.PARTIAL_P_API)) {
    data = data.replace(C.PARTIAL_P_API, API_PUBLIC)
  }

  if (data.includes(C.PARTIAL_U_ACCOUNT_ID)) {
    data = data.replace(C.PARTIAL_U_ACCOUNT_ID, uAccountId)
  }

  if (data.includes(C.PARTIAL_U_BASE)) {
    data = data.replace(C.PARTIAL_U_BASE, API_UNITE)
  }

  if (data.includes(C.PARTIAL_U_TOKEN) && token) {
    data = data.replace(C.PARTIAL_U_TOKEN, token)
  }

  if (data.includes(C.PARTIAL_U_USER_ID)) {
    data = data.replace(C.PARTIAL_U_USER_ID, uUserId)
  }

  return data
}

const baseFetch = async (
  method,
  url,
  customHeaders = {},
  bodyData,
  encode = true,
) => {
  const {
    auth: { token, accountId },
  } = store.getState()
  if (!accountId && !token) {
    store.dispatch(hydrateUniteFailure())
  }

  const headers = {
    Authorization: `Bearer ${token}`,
    Accept: 'application/json',
    'x-ACCEPT-VERSION': 'v1.1',
    'Content-Type': 'application/json',
    'unite.user_agent': userAgent,
    ...customHeaders,
  }

  let body = bodyData || undefined

  if (body && encode) {
    body = interpolate(body)
  }

  const fetchUrl = interpolate(url)
  const fetchConfig = {
    method,
    headers,
    body,
  }

  // TODO handle timeout from server https://stackoverflow.com/questions/46946380/fetch-api-request-timeout
  return fetch(fetchUrl, fetchConfig).then(checkStatus).then(parseJSON)
}

const Request = {
  get: ({ url, headers }) => baseFetch('GET', url, headers),
  post: ({ url, data, headers }, encode) =>
    baseFetch('POST', url, headers, data, encode),
  delete: ({ url, data, headers }, encode) =>
    baseFetch('DELETE', url, headers, data, encode),
  patch: ({ url, data, headers }, encode) =>
    baseFetch('PATCH', url, headers, data, encode),
  put: ({ url, data, headers }, encode) =>
    baseFetch('PUT', url, headers, data, encode),
  head: ({ url, data, headers }, encode) =>
    baseFetch('HEAD', url, headers, data, encode),
}

export default Request
